/**
 * DreamSanctuary.jsx - The Astral Dream Museum
 * 
 * Features:
 * - Voice input with Gemini Speech-to-Text (professional quality)
 * - Image upload for vision analysis
 * - Multilingual support (auto-detects and responds in same language)
 * - Text-to-Speech output (browser native)
 * - Dream visualization generation (Manifest Vision)
 * - Deeper analysis (Unveil Hidden Truths)
 * - Card-based gallery (The Astral Journal)
 * - 3-pillar navigation: Cosmos, Vínculos, Sueños
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Upload, Volume2, VolumeX, Sparkles, Eye, X, ChevronRight, ImageIcon, BookOpen, Share2, Download, Loader2, AlertCircle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useGeminiVoiceInput } from "../hooks/useGeminiVoiceInput";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Language code to full name mapping
const LANGUAGE_NAMES = {
    en: "English", es: "Spanish", zh: "Chinese", hi: "Hindi", 
    nl: "Dutch", ja: "Japanese", ar: "Arabic", fr: "French", 
    de: "German", pt: "Portuguese", it: "Italian", ko: "Korean"
};

// ============================================
// CELESTIAL INVITATION - TAROT CARD GENERATOR
// ============================================
async function generateTarotCard(imageUrl, insightText, title = "The Oneiros Spoke") {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // 9:16 aspect ratio (1080x1920 for Stories)
        canvas.width = 1080;
        canvas.height = 1920;
        
        // Load the dream image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
            // === BACKGROUND: Dream Image (top 70%) ===
            const imageHeight = canvas.height * 0.7;
            
            // Calculate cover-style scaling
            const scale = Math.max(canvas.width / img.width, imageHeight / img.height);
            const scaledWidth = img.width * scale;
            const scaledHeight = img.height * scale;
            const offsetX = (canvas.width - scaledWidth) / 2;
            const offsetY = (imageHeight - scaledHeight) / 2;
            
            // Draw dream image
            ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);
            
            // Add gradient overlay on image for text readability
            const imageGradient = ctx.createLinearGradient(0, imageHeight * 0.5, 0, imageHeight);
            imageGradient.addColorStop(0, 'rgba(0,0,0,0)');
            imageGradient.addColorStop(1, 'rgba(15,10,25,0.9)');
            ctx.fillStyle = imageGradient;
            ctx.fillRect(0, 0, canvas.width, imageHeight);
            
            // === FOOTER: Mystical Gradient (bottom 30%) ===
            const footerY = imageHeight;
            const footerHeight = canvas.height - imageHeight;
            
            const footerGradient = ctx.createLinearGradient(0, footerY, 0, canvas.height);
            footerGradient.addColorStop(0, 'rgba(15,10,25,1)');
            footerGradient.addColorStop(0.3, 'rgba(30,20,50,1)');
            footerGradient.addColorStop(1, 'rgba(10,5,20,1)');
            ctx.fillStyle = footerGradient;
            ctx.fillRect(0, footerY, canvas.width, footerHeight);
            
            // Add subtle purple glow
            const glowGradient = ctx.createRadialGradient(
                canvas.width / 2, footerY + footerHeight / 2, 0,
                canvas.width / 2, footerY + footerHeight / 2, canvas.width * 0.6
            );
            glowGradient.addColorStop(0, 'rgba(147,112,219,0.15)');
            glowGradient.addColorStop(1, 'rgba(147,112,219,0)');
            ctx.fillStyle = glowGradient;
            ctx.fillRect(0, footerY, canvas.width, footerHeight);
            
            // === DECORATIVE ELEMENTS ===
            // Top corners
            ctx.strokeStyle = 'rgba(212,175,55,0.4)';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(40, 80); ctx.lineTo(40, 40); ctx.lineTo(80, 40);
            ctx.moveTo(canvas.width - 40, 80); ctx.lineTo(canvas.width - 40, 40); ctx.lineTo(canvas.width - 80, 40);
            ctx.stroke();
            
            // Bottom corners
            ctx.beginPath();
            ctx.moveTo(40, canvas.height - 80); ctx.lineTo(40, canvas.height - 40); ctx.lineTo(80, canvas.height - 40);
            ctx.moveTo(canvas.width - 40, canvas.height - 80); ctx.lineTo(canvas.width - 40, canvas.height - 40); ctx.lineTo(canvas.width - 80, canvas.height - 40);
            ctx.stroke();
            
            // Separator line
            ctx.strokeStyle = 'rgba(212,175,55,0.5)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(100, footerY + 40);
            ctx.lineTo(canvas.width - 100, footerY + 40);
            ctx.stroke();
            
            // === TEXT: Title ===
            ctx.fillStyle = '#D4AF37';
            ctx.font = '600 56px Cinzel, serif';
            ctx.textAlign = 'center';
            ctx.fillText(title.toUpperCase(), canvas.width / 2, footerY + 120);
            
            // === TEXT: Insight (truncated) ===
            // Extract first sentence or first 150 chars
            let insight = insightText || "Your dream holds profound meaning...";
            // Remove markdown formatting
            insight = insight.replace(/\*\*/g, '').replace(/[#*_]/g, '');
            // Get first sentence
            const firstSentence = insight.match(/^[^.!?]+[.!?]/);
            if (firstSentence) {
                insight = firstSentence[0];
            }
            if (insight.length > 150) {
                insight = insight.substring(0, 147) + '...';
            }
            
            ctx.fillStyle = 'rgba(255,255,255,0.85)';
            ctx.font = '400 36px Inter, sans-serif';
            
            // Word wrap the insight text
            const maxWidth = canvas.width - 160;
            const words = insight.split(' ');
            let lines = [];
            let currentLine = '';
            
            words.forEach(word => {
                const testLine = currentLine + (currentLine ? ' ' : '') + word;
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine = testLine;
                }
            });
            if (currentLine) lines.push(currentLine);
            
            // Draw wrapped text (max 4 lines)
            lines = lines.slice(0, 4);
            const lineHeight = 52;
            const textStartY = footerY + 200;
            lines.forEach((line, i) => {
                ctx.fillText(line, canvas.width / 2, textStartY + i * lineHeight);
            });
            
            // === BRANDING ===
            // App name
            ctx.fillStyle = 'rgba(212,175,55,0.7)';
            ctx.font = '500 32px Cinzel, serif';
            ctx.fillText('✦ ASTRAL BELONGING ✦', canvas.width / 2, canvas.height - 120);
            
            // Tagline
            ctx.fillStyle = 'rgba(255,255,255,0.4)';
            ctx.font = '300 24px Inter, sans-serif';
            ctx.fillText('Discover Your Cosmic Truth', canvas.width / 2, canvas.height - 70);
            
            // Convert to blob
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to generate image'));
                }
            }, 'image/png', 1.0);
        };
        
        img.onerror = () => {
            reject(new Error('Failed to load dream image'));
        };
        
        img.src = imageUrl;
    });
}

// Share or download the Tarot Card
async function shareTarotCard(imageUrl, insightText) {
    try {
        const blob = await generateTarotCard(imageUrl, insightText);
        const file = new File([blob], 'dream-vision.png', { type: 'image/png' });
        
        // Try native share (mobile)
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
                title: 'My Dream Vision',
                text: 'The Oneiros revealed my dream\'s meaning ✨',
                files: [file]
            });
            return { success: true, method: 'share' };
        }
        
        // Fallback: Download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'dream-vision.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return { success: true, method: 'download' };
        
    } catch (err) {
        console.error('[Share] Error:', err);
        return { success: false, error: err.message };
    }
}

// ============================================
// TEXT-TO-SPEECH HOOK
// ============================================
function useTextToSpeech() {
    const [isSpeaking, setIsSpeaking] = useState(false);
    const utteranceRef = useRef(null);

    const speak = useCallback((text) => {
        if (!window.speechSynthesis) return;
        
        // Cancel any ongoing speech
        window.speechSynthesis.cancel();
        
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        
        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
    }, []);

    const stop = useCallback(() => {
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
    }, []);

    return { speak, stop, isSpeaking };
}

// ============================================
// DREAM CHAT MESSAGE COMPONENT
// ============================================
function ChatMessage({ message, onSpeak, isSpeaking }) {
    const isUser = message.role === 'user';
    
    return (
        <motion.div
            className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
        >
            <div 
                className={`max-w-[85%] rounded-2xl p-4 ${isUser 
                    ? 'bg-purple-900/40 border border-purple-500/30' 
                    : 'bg-black/40 border border-primary/20'
                }`}
            >
                {message.isInterim && (
                    <span className="text-sm text-purple-300/60 italic">{message.content}</span>
                )}
                {!message.isInterim && (
                    <>
                        <p className="text-sm text-white/80 whitespace-pre-wrap">{message.content}</p>
                        {!isUser && message.content && (
                            <button
                                onClick={() => onSpeak(message.content)}
                                className="mt-2 flex items-center gap-1 text-[10px] text-primary/60 hover:text-primary transition-colors"
                            >
                                {isSpeaking ? <VolumeX size={12} /> : <Volume2 size={12} />}
                                <span>{isSpeaking ? 'Stop' : 'Listen'}</span>
                            </button>
                        )}
                    </>
                )}
            </div>
        </motion.div>
    );
}

// ============================================
// SHARE VISION BUTTON COMPONENT
// ============================================
function ShareVisionButton({ imageUrl, insightText }) {
    const [isSharing, setIsSharing] = useState(false);
    const [shareResult, setShareResult] = useState(null);
    
    const handleShare = async () => {
        setIsSharing(true);
        setShareResult(null);
        
        try {
            const result = await shareTarotCard(imageUrl, insightText);
            setShareResult(result);
            
            // Auto-hide success message after 3s
            if (result.success) {
                setTimeout(() => setShareResult(null), 3000);
            }
        } catch (err) {
            setShareResult({ success: false, error: err.message });
        } finally {
            setIsSharing(false);
        }
    };
    
    return (
        <div className="mt-3 flex items-center gap-3">
            <motion.button
                onClick={handleShare}
                disabled={isSharing}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-inter disabled:opacity-50"
                style={{ 
                    background: 'linear-gradient(135deg, rgba(212,175,55,0.2) 0%, rgba(180,150,50,0.3) 100%)', 
                    border: '1px solid rgba(212,175,55,0.4)',
                    color: '#D4AF37'
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="share-vision-btn"
            >
                {isSharing ? (
                    <>
                        <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        <span>Creating...</span>
                    </>
                ) : (
                    <>
                        <Share2 size={14} />
                        <span>{t('dreams.share_vision')}</span>
                    </>
                )}
            </motion.button>
            
            {/* Result feedback */}
            <AnimatePresence>
                {shareResult && (
                    <motion.span 
                        className={`text-xs ${shareResult.success ? 'text-green-400' : 'text-red-400'}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                    >
                        {shareResult.success 
                            ? (shareResult.method === 'download' ? `✓ ${t('dreams.downloaded')}` : `✓ ${t('dreams.shared')}`)
                            : `✗ ${shareResult.error}`
                        }
                    </motion.span>
                )}
            </AnimatePresence>
        </div>
    );
}

// ============================================
// DREAM INTERPRETER (THE ONEIROS)
// ============================================
function DreamInterpreter({ onClose, onDreamSaved }) {
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [currentDream, setCurrentDream] = useState(null);
    const [isVisualizing, setIsVisualizing] = useState(false);
    const [isDeepening, setIsDeepening] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const fileInputRef = useRef(null);
    const chatContainerRef = useRef(null);
    
    // Get global language setting
    const { language, t } = useLanguage();
    const targetLanguage = LANGUAGE_NAMES[language] || "English";
    
    // Gemini Voice Input Hook - Professional quality transcription
    const { 
        isRecording, 
        isTranscribing, 
        transcript, 
        error: voiceError, 
        isSupported: voiceSupported, 
        recordingTime,
        maxRecordingTime,
        toggleRecording, 
        clearTranscript,
        formatTime,
        languageDisplay,
        waveformData,
        languageIcon
    } = useGeminiVoiceInput(language);
    const { speak, stop, isSpeaking } = useTextToSpeech();

    // Scroll to bottom when messages change
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    // Update input with voice transcript (Gemini STT)
    useEffect(() => {
        if (transcript) {
            setInputText(prev => (prev ? prev + " " + transcript : transcript).trim());
            clearTranscript();
        }
    }, [transcript, clearTranscript]);

    const handleSend = async () => {
        const text = inputText.trim();
        if (text.length < 3 && !uploadedImage) return;

        // Add user message
        const userMessage = { role: 'user', content: text || '[Image uploaded]' };
        setMessages(prev => [...prev, userMessage]);
        setInputText("");
        setIsLoading(true);

        try {
            let response;
            
            if (uploadedImage) {
                // Analyze image - extract base64 data
                let imageData = uploadedImage;
                
                // === DIAGNOSTIC LOGGING ===
                console.log('[Dream Sanctuary] Preparing image upload...');
                console.log('[Dream Sanctuary] Image size:', uploadedImage?.length);
                
                if (uploadedImage.includes(',')) {
                    imageData = uploadedImage.split(',')[1];
                    console.log('[Dream Sanctuary] Base64 data extracted, size:', imageData?.length);
                }
                
                const requestBody = {
                    image_base64: imageData,
                    language: targetLanguage
                };
                
                console.log('[Dream Sanctuary DEBUG] Request body keys:', Object.keys(requestBody));
                console.log('[Dream Sanctuary DEBUG] image_base64 length in body:', requestBody.image_base64?.length);
                console.log('[Dream Sanctuary DEBUG] API_URL:', API_URL);
                console.log('[Dream Sanctuary DEBUG] Full endpoint:', `${API_URL}/api/dream/analyze-image`);
                // === DIAGNOSTIC LOGGING END ===
                
                try {
                    response = await fetch(`${API_URL}/api/dream/analyze-image`, {
                        method: "POST",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(requestBody)
                    });
                    console.log('[Dream Sanctuary] Response status:', response.status);
                } catch (fetchError) {
                    console.error('[Dream Sanctuary] Network error:', fetchError.message);
                    throw fetchError;
                }
                
                setUploadedImage(null);
            } else {
                // Analyze dream text
                try {
                    response = await fetch(`${API_URL}/api/dream/analyze`, {
                        method: "POST",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ dream_text: text, language: targetLanguage })
                    });
                } catch (fetchError) {
                    console.error('[Dream Sanctuary] Network error:', fetchError.message);
                    throw fetchError;
                }
            }

            if (!response.ok) {
                let errorText = "";
                try {
                    errorText = await response.text();
                } catch (e) {
                    errorText = "Could not read error response";
                }
                
                console.error('[Dream Sanctuary] API Error:', response.status, errorText);
                
                let errorData = {};
                try {
                    errorData = JSON.parse(errorText);
                } catch (parseError) {
                    // Not JSON
                }
                
                throw new Error(errorData.detail || `Analysis failed (${response.status})`);
            }

            const data = await response.json();
            console.log('[Dream Sanctuary] Analysis received successfully');
            
            // Format response
            let oracleResponse = "";
            if (data.summary) {
                oracleResponse = `✨ **The Vision**\n${data.summary}\n\n`;
            }
            if (data.deep_meaning) {
                oracleResponse += `🌙 **Deep Meaning**\n${data.deep_meaning}\n\n`;
            }
            if (data.symbols && data.symbols.length > 0) {
                oracleResponse += `🔮 **Symbols**\n${data.symbols.map(s => `• ${s.symbol}: ${s.meaning}`).join('\n')}\n\n`;
            }
            if (data.action_for_today) {
                oracleResponse += `⚡ **Action for Today**\n${data.action_for_today}`;
            }
            if (data.vision_summary) {
                // Image analysis response
                oracleResponse = `👁 **Vision Analysis**\n${data.vision_summary}\n\n`;
                if (data.symbols_detected && data.symbols_detected.length > 0) {
                    oracleResponse += `🔮 **Symbols Detected**\n${data.symbols_detected.map(s => `• ${s.symbol}: ${s.meaning}`).join('\n')}\n\n`;
                }
                if (data.emotional_tone) oracleResponse += `💫 **Emotional Tone**: ${data.emotional_tone}\n\n`;
                if (data.guidance) oracleResponse += `🌟 **Guidance**: ${data.guidance}`;
            }

            setMessages(prev => [...prev, { role: 'oracle', content: oracleResponse }]);
            
            if (data.dream_id) {
                setCurrentDream(data);
            }

        } catch (err) {
            console.error("[Dream Sanctuary] Error:", err.message);
            
            const errorMsg = err.message || "Unknown error";
            
            // Provide specific guidance based on error type
            let guidance = t('dreams.try_again');
            if (errorMsg.includes("limit")) {
                guidance = t('dreams.daily_limit');
            } else if (errorMsg.includes("invalid") || errorMsg.includes("image")) {
                guidance = t('dreams.image_error');
            } else if (errorMsg.includes("NetworkError") || errorMsg.includes("Failed to fetch")) {
                guidance = t('dreams.network_error');
            }
            
            setMessages(prev => [...prev, { 
                role: 'oracle', 
                content: `${t('dreams.error_occurred')}: ${errorMsg}\n\n${guidance}` 
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleManifestVision = async () => {
        if (!currentDream?.dream_id) return;
        
        setIsVisualizing(true);
        try {
            const response = await fetch(`${API_URL}/api/dream/visualize`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ dream_id: currentDream.dream_id })
            });

            if (!response.ok) throw new Error(t('dreams.visualization_failed'));

            const data = await response.json();
            if (data.image_url) {
                setMessages(prev => [...prev, {
                    role: 'oracle',
                    content: `🎨 **${t('dreams.manifest_vision')}**\n${t('dreams.vision_crystallized')}`,
                    image: data.image_url
                }]);
                setCurrentDream(prev => ({ ...prev, image_url: data.image_url }));
                onDreamSaved?.();
            }
        } catch (err) {
            console.error("Visualization error:", err);
        } finally {
            setIsVisualizing(false);
        }
    };

    const handleUnveilTruths = async () => {
        if (!currentDream?.dream_id) return;
        
        setIsDeepening(true);
        try {
            const response = await fetch(`${API_URL}/api/dream/deepen`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ dream_id: currentDream.dream_id })
            });

            if (!response.ok) throw new Error(t('dreams.deepen_failed'));

            const data = await response.json();
            let deepContent = "🔮 **Hidden Truths Unveiled**\n\n";
            if (data.hidden_message) deepContent += `**The Hidden Message**: ${data.hidden_message}\n\n`;
            if (data.ancestral_pattern) deepContent += `**Ancestral Pattern**: ${data.ancestral_pattern}\n\n`;
            if (data.predictive_insight) deepContent += `**Predictive Insight**: ${data.predictive_insight}\n\n`;
            if (data.shadow_integration) deepContent += `**Shadow Work**: ${data.shadow_integration}\n\n`;
            if (data.ritual_practice) deepContent += `**Ritual Practice**: ${data.ritual_practice}\n\n`;
            if (data.oracle_wisdom) deepContent += `✨ ${data.oracle_wisdom}`;

            setMessages(prev => [...prev, { role: 'oracle', content: deepContent }]);
            onDreamSaved?.();
        } catch (err) {
            console.error("Deepen error:", err);
        } finally {
            setIsDeepening(false);
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setUploadedImage(event.target?.result);
        };
        reader.readAsDataURL(file);
    };

    return (
        <motion.div
            className="fixed inset-0 z-50 flex flex-col"
            style={{ background: 'linear-gradient(180deg, rgba(15,12,25,0.98) 0%, rgba(8,6,15,0.99) 100%)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            {/* Header */}
            <div className="flex-shrink-0 p-4 border-b border-purple-500/20 flex items-center justify-between">
                <div>
                    <h2 className="font-cinzel text-xl" style={{ background: 'linear-gradient(135deg, #9C27B0 0%, #E040FB 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        The Oneiros
                    </h2>
                    <p className="text-[10px] font-inter text-purple-300/50 uppercase tracking-widest">Dream Interpreter</p>
                </div>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                >
                    <X size={20} />
                </button>
            </div>

            {/* Chat Messages */}
            <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && (
                    <div className="text-center py-12">
                        <span className="text-5xl mb-4 block">👁</span>
                        <p className="text-lg font-cinzel text-white/60 mb-2">Speak or Write Your Dream</p>
                        <p className="text-sm font-inter text-white/40 max-w-sm mx-auto">
                            Describe your nocturnal vision... The Oneiros will reveal its hidden meanings.
                        </p>
                    </div>
                )}

                {messages.map((msg, idx) => (
                    <div key={idx}>
                        <ChatMessage 
                            message={msg} 
                            onSpeak={isSpeaking ? stop : speak}
                            isSpeaking={isSpeaking}
                        />
                        {msg.image && (
                            <motion.div
                                className="ml-4 mt-2"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                            >
                                <img 
                                    src={msg.image} 
                                    alt="Dream vision" 
                                    className="max-w-[80%] rounded-xl border border-purple-500/30"
                                />
                                {/* Share Vision Button */}
                                <ShareVisionButton 
                                    imageUrl={msg.image} 
                                    insightText={messages.find(m => m.role === 'oracle' && !m.image)?.content || ''} 
                                />
                            </motion.div>
                        )}
                    </div>
                ))}

                {/* Recording indicator (real-time voice) */}
                {isRecording && (
                    <ChatMessage 
                        message={{ 
                            role: 'user', 
                            content: '🎙️ Recording...', 
                            isInterim: true 
                        }}
                        onSpeak={() => {}}
                        isSpeaking={false}
                    />
                )}

                {isLoading && (
                    <div className="flex justify-start">
                        <div className="bg-black/40 border border-primary/20 rounded-2xl p-4">
                            <div className="flex gap-2">
                                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Action Buttons (appear after interpretation) */}
            {currentDream && (
                <div className="flex-shrink-0 px-4 py-3 border-t border-purple-500/10 flex gap-3">
                    <motion.button
                        onClick={handleManifestVision}
                        disabled={isVisualizing || currentDream.image_url}
                        className="flex-1 py-3 rounded-xl text-sm font-inter flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, rgba(147,112,219,0.2) 0%, rgba(75,0,130,0.3) 100%)', border: '1px solid rgba(147,112,219,0.4)' }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <ImageIcon size={16} />
                        {isVisualizing ? 'Manifesting...' : 'Manifest Vision'}
                    </motion.button>
                    <motion.button
                        onClick={handleUnveilTruths}
                        disabled={isDeepening}
                        className="flex-1 py-3 rounded-xl text-sm font-inter flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(180,150,50,0.2) 100%)', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <Eye size={16} />
                        {isDeepening ? 'Unveiling...' : 'Unveil Hidden Truths'}
                    </motion.button>
                </div>
            )}

            {/* Image Preview */}
            {uploadedImage && (
                <div className="px-4 py-2 border-t border-purple-500/10">
                    <div className="relative inline-block">
                        <img src={uploadedImage} alt="Upload" className="h-16 rounded-lg opacity-80" />
                        <button
                            onClick={() => setUploadedImage(null)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Voice Error */}
            {voiceError && (
                <motion.div 
                    className="mx-4 mb-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <p className="text-xs text-red-400 flex items-center gap-2">
                        <AlertCircle size={12} />
                        {voiceError}
                    </p>
                </motion.div>
            )}

            {/* Input Bar */}
            <div className="flex-shrink-0 p-4 border-t border-purple-500/20">
                {/* Recording Indicator */}
                <AnimatePresence>
                    {(isRecording || isTranscribing) && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="flex items-center justify-center gap-3 mb-3 p-2 rounded-lg"
                            style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                        >
                            {isRecording ? (
                                <>
                                    {/* Waveform Visualizer - CSS-based for performance */}
                                    <div className="flex items-center gap-0.5 h-6">
                                        {waveformData.length > 0 ? (
                                            waveformData.map((level, i) => (
                                                <div
                                                    key={i}
                                                    className="w-1 bg-purple-400 rounded-full transition-all duration-75"
                                                    style={{ 
                                                        height: `${Math.max(4, level * 0.24)}px`,
                                                        minHeight: '4px'
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <motion.div
                                                className="w-3 h-3 rounded-full bg-red-500"
                                                animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                                                transition={{ duration: 1, repeat: Infinity }}
                                            />
                                        )}
                                    </div>
                                    <span className="text-red-400 text-sm font-medium">
                                        {formatTime(recordingTime)} / {formatTime(maxRecordingTime)}
                                    </span>
                                    <span className="text-purple-300/80 text-sm">{languageIcon}</span>
                                </>
                            ) : (
                                <>
                                    <Loader2 size={16} className="text-purple-400 animate-spin" />
                                    <span className="text-purple-400 text-sm">{t('chat.transcribing') || 'Transcribing...'}</span>
                                </>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="flex items-center gap-3">
                    {/* Voice Input - Gemini Speech-to-Text */}
                    {voiceSupported && (
                        <motion.button
                            onClick={toggleRecording}
                            disabled={isTranscribing}
                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isRecording ? 'bg-red-500' : 'bg-purple-900/50 hover:bg-purple-900/70'}`}
                            style={{ 
                                border: isRecording ? '2px solid rgba(255,100,100,0.8)' : '1px solid rgba(147,112,219,0.4)',
                                boxShadow: isRecording ? '0 0 15px rgba(239, 68, 68, 0.5)' : 'none'
                            }}
                            whileHover={{ scale: isTranscribing ? 1 : 1.05 }}
                            whileTap={{ scale: isTranscribing ? 1 : 0.95 }}
                            animate={isRecording ? { scale: [1, 1.05, 1] } : {}}
                            transition={isRecording ? { duration: 1, repeat: Infinity } : {}}
                            data-testid="dream-voice-btn"
                        >
                            {isTranscribing ? (
                                <Loader2 size={20} className="text-purple-300 animate-spin" />
                            ) : isRecording ? (
                                <MicOff size={20} className="text-white" />
                            ) : (
                                <Mic size={20} className="text-purple-300" />
                            )}
                        </motion.button>
                    )}

                    {/* Image Upload */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        className="hidden"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-12 h-12 rounded-full flex items-center justify-center bg-purple-900/50 hover:bg-purple-900/70 transition-colors"
                        style={{ border: '1px solid rgba(147,112,219,0.4)' }}
                    >
                        <Upload size={18} className="text-purple-300" />
                    </button>

                    {/* Text Input */}
                    <div className="flex-1 relative">
                        <input
                            type="text"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && inputText.trim().length >= 3 && handleSend()}
                            placeholder={isRecording 
                                ? (t('chat.recording') || 'Recording...') 
                                : isTranscribing 
                                ? (t('chat.transcribing') || 'Transcribing...') 
                                : t('dreams.describe_dream')}
                            className="w-full bg-black/30 border border-purple-500/20 rounded-full px-5 py-3 text-sm font-inter text-white/80 placeholder:text-white/30 focus:outline-none focus:border-purple-500/50"
                            disabled={isRecording || isTranscribing}
                        />
                        {/* Validation legend */}
                        {inputText.trim().length > 0 && inputText.trim().length < 3 && !uploadedImage && (
                            <span className="absolute right-16 top-1/2 -translate-y-1/2 text-[10px] text-amber-400/70">
                                {t('dreams.chars_needed')}
                            </span>
                        )}
                    </div>

                    {/* Send Button */}
                    <motion.button
                        onClick={handleSend}
                        disabled={(inputText.trim().length < 3 && !uploadedImage) || isLoading}
                        className="w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, rgba(147,112,219,0.6) 0%, rgba(75,0,130,0.7) 100%)', border: '1px solid rgba(147,112,219,0.5)' }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        data-testid="dream-send-btn"
                    >
                        <Sparkles size={18} className="text-white" />
                    </motion.button>
                </div>
            </div>
        </motion.div>
    );
}

// ============================================
// ASTRAL JOURNAL - CARD GALLERY
// ============================================
function AstralJournal({ onOpenDream }) {
    const { t } = useLanguage();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEntry, setSelectedEntry] = useState(null);

    const fetchEntries = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/dream/journal`, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setEntries(data.entries || []);
            }
        } catch (err) {
            console.error("Failed to fetch journal:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Defer fetch to not block initial render
    useEffect(() => {
        // Small delay to let UI render first
        const timer = setTimeout(() => {
            fetchEntries();
        }, 100);
        return () => clearTimeout(timer);
    }, [fetchEntries]);

    // Don't show loading spinner - show empty state immediately
    // This prevents blocking the main UI

    if (entries.length === 0) {
        return (
            <div className="text-center py-16">
                <span className="text-6xl mb-4 block">🌙</span>
                <p className="text-lg font-cinzel text-white/60 mb-2">{t('dashboard.journal_awaits') || 'Your Journal Awaits'}</p>
                <p className="text-sm font-inter text-white/40 max-w-xs mx-auto mb-6">
                    {t('dashboard.journal_desc') || 'Dreams you interpret will appear here as entries in your astral museum.'}
                </p>
                <motion.button
                    onClick={onOpenDream}
                    className="px-6 py-3 rounded-xl text-sm font-inter"
                    style={{ background: 'linear-gradient(135deg, rgba(147,112,219,0.3) 0%, rgba(75,0,130,0.4) 100%)', border: '1px solid rgba(147,112,219,0.5)' }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    <span className="mr-2">👁</span> {t('dashboard.record_first_dream')}
                </motion.button>
            </div>
        );
    }

    return (
        <div className="py-4">
            <div className="flex items-center justify-between mb-6 px-2">
                <p className="text-sm font-inter text-white/50">{entries.length} {t('dashboard.dreams_in_journal')}</p>
                <motion.button
                    onClick={onOpenDream}
                    className="px-4 py-2 rounded-lg text-xs font-inter flex items-center gap-2"
                    style={{ background: 'rgba(147,112,219,0.2)', border: '1px solid rgba(147,112,219,0.3)' }}
                    whileHover={{ scale: 1.02 }}
                >
                    <span>+</span> {t('dashboard.new_dream') || 'New Dream'}
                </motion.button>
            </div>

            {/* Card Grid */}
            <div className="grid grid-cols-2 gap-4">
                {entries.map((entry, idx) => (
                    <motion.div
                        key={entry.dream_id || idx}
                        className="rounded-xl overflow-hidden cursor-pointer group"
                        style={{ 
                            background: 'linear-gradient(135deg, rgba(30,25,45,0.9) 0%, rgba(20,15,30,0.95) 100%)', 
                            border: '1px solid rgba(147,112,219,0.2)' 
                        }}
                        whileHover={{ borderColor: 'rgba(147,112,219,0.5)', y: -4, scale: 1.02 }}
                        onClick={() => setSelectedEntry(entry)}
                    >
                        {/* Thumbnail */}
                        {entry.image_url ? (
                            <div className="h-32 overflow-hidden relative">
                                <img src={entry.image_url} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(20,15,30,0.98)] via-transparent to-transparent" />
                            </div>
                        ) : (
                            <div className="h-32 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(147,112,219,0.1) 0%, rgba(75,0,130,0.2) 100%)' }}>
                                <span className="text-4xl opacity-30">🌙</span>
                            </div>
                        )}

                        {/* Content */}
                        <div className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[10px] font-inter uppercase tracking-wider px-3 py-1 rounded-full" style={{ background: 'rgba(147,112,219,0.2)', color: 'rgba(147,112,219,0.9)' }}>
                                    {entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                                </span>
                                <div className="flex gap-1">
                                    {entry.has_image && <ImageIcon size={12} className="text-purple-400/60" />}
                                    {entry.has_deeper && <Eye size={12} className="text-primary/60" />}
                                </div>
                            </div>
                            <p className="text-sm font-inter text-white/70 line-clamp-2 leading-relaxed">
                                {entry.summary || entry.raw_transcript?.substring(0, 80) + '...'}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Detail Modal */}
            <AnimatePresence>
                {selectedEntry && (
                    <motion.div 
                        className="fixed inset-0 z-[70] flex items-center justify-center p-6" 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }}
                    >
                        <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={() => setSelectedEntry(null)} />
                        <motion.div 
                            className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl" 
                            style={{ background: 'linear-gradient(180deg, rgba(30,25,45,0.98) 0%, rgba(15,12,25,0.99) 100%)', border: '1px solid rgba(147,112,219,0.3)' }} 
                            initial={{ scale: 0.9, y: 30 }} 
                            animate={{ scale: 1, y: 0 }} 
                            exit={{ scale: 0.9, y: 30 }}
                        >
                            <button 
                                onClick={() => setSelectedEntry(null)} 
                                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                            >
                                <X size={20} />
                            </button>

                            {/* High-Res Image */}
                            {selectedEntry.image_url && (
                                <img 
                                    src={selectedEntry.image_url} 
                                    alt="Dream vision" 
                                    className="w-full h-56 object-cover rounded-t-2xl"
                                />
                            )}

                            <div className="p-6 space-y-5">
                                {/* Date */}
                                <p className="text-xs text-white/40">
                                    {selectedEntry.created_at && new Date(selectedEntry.created_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                </p>

                                {/* Dream Text */}
                                <div>
                                    <h4 className="text-[10px] font-inter text-purple-300/50 uppercase tracking-widest mb-2">The Dream</h4>
                                    <p className="text-sm text-white/70 leading-relaxed italic">
                                        "{selectedEntry.raw_transcript || selectedEntry.dream_text}"
                                    </p>
                                </div>

                                {/* Interpretation */}
                                {selectedEntry.deep_meaning && (
                                    <div className="p-4 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
                                        <h4 className="text-[10px] font-inter text-purple-300/50 uppercase tracking-widest mb-2">Oracle's Interpretation</h4>
                                        <p className="text-sm text-white/75 leading-relaxed">{selectedEntry.deep_meaning}</p>
                                    </div>
                                )}

                                {/* Hidden Truths */}
                                {selectedEntry.deeper_analysis?.hidden_message && (
                                    <div className="p-4 rounded-xl" style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)' }}>
                                        <h4 className="text-[10px] font-inter text-primary/70 uppercase tracking-widest mb-2">Hidden Truths</h4>
                                        <p className="text-sm text-white/75 leading-relaxed">{selectedEntry.deeper_analysis.hidden_message}</p>
                                    </div>
                                )}

                                {/* Symbols */}
                                {selectedEntry.symbols && selectedEntry.symbols.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {selectedEntry.symbols.map((s, i) => (
                                            <span key={i} className="px-3 py-1.5 rounded-full text-xs" style={{ background: 'rgba(147,112,219,0.2)', color: 'rgba(147,112,219,0.9)' }}>
                                                {s.symbol}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ============================================
// MAIN DREAM SANCTUARY COMPONENT - 3 PILLARS
// ============================================
export function DreamSanctuary() {
    const { t, language } = useLanguage();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDreamOpen, setIsDreamOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('journal');
    const [refreshKey, setRefreshKey] = useState(0);
    
    // State for 3-pillar navigation
    const [activeSection, setActiveSection] = useState(null); // 'cosmos' | 'vinculos' | 'suenos' | null
    const [prefilledQuery, setPrefilledQuery] = useState(''); // Pre-filled text from Today's Commands

    const handleDreamSaved = () => {
        setRefreshKey(k => k + 1);
    };
    
    // Listen for "Ask the Oracle" events from Today's Commands
    useEffect(() => {
        const handleOpenOracle = (event) => {
            const { query, oracle } = event.detail;
            console.log('[DreamSanctuary] Opening Oracle with query:', query, 'oracle:', oracle);
            
            // Set the pre-filled query
            setPrefilledQuery(query || '');
            
            // Open the appropriate oracle
            if (oracle === 'vinculos' || oracle === 'love') {
                setActiveSection('vinculos');
            } else if (oracle === 'suenos' || oracle === 'dreams') {
                setActiveSection('suenos');
            } else {
                // Default to Cosmos
                setActiveSection('cosmos');
            }
        };
        
        window.addEventListener('openOracleWithQuery', handleOpenOracle);
        return () => window.removeEventListener('openOracleWithQuery', handleOpenOracle);
    }, []);
    
    // Clear prefilled query when oracle is closed
    const handleCloseOracle = () => {
        setActiveSection(null);
        setPrefilledQuery('');
    };
    
    // Import chat components dynamically to avoid circular deps
    const CosmosChat = require('./CosmosChat').default;
    const { LoveChat, DreamsChat } = require('./UnifiedChat');

    // Pillar definitions with Cosmic Luxury themes
    const pillars = [
        {
            id: 'cosmos',
            titleEs: 'COSMOS',
            titleEn: 'COSMOS',
            subtitleEs: 'El Navegante',
            subtitleEn: 'The Navigator',
            // North Star / Compass Rose SVG
            icon: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 2L17.5 14.5L30 16L17.5 17.5L16 30L14.5 17.5L2 16L14.5 14.5L16 2Z" stroke="#D4AF37" strokeWidth="0.75" fill="none" opacity="0.9"/>
                    <path d="M16 6L17 14L24 16L17 18L16 26L15 18L8 16L15 14L16 6Z" stroke="#60A5FA" strokeWidth="0.5" fill="rgba(96,165,250,0.1)"/>
                    <circle cx="16" cy="16" r="2" fill="#60A5FA" opacity="0.8"/>
                    <circle cx="16" cy="16" r="6" stroke="#60A5FA" strokeWidth="0.3" fill="none" opacity="0.4"/>
                </svg>
            ),
            gradient: 'radial-gradient(ellipse at top, #1e3a5f 0%, #0B0F19 40%, #000000 100%)',
            borderColor: 'rgba(96, 165, 250, 0.25)',
            glowColor: 'rgba(96, 165, 250, 0.15)',
            textColor: '#60A5FA',
            textShadow: 'none'
        },
        {
            id: 'vinculos',
            titleEs: 'VÍNCULOS',
            titleEn: 'BONDS',
            subtitleEs: 'El Sanador',
            subtitleEn: 'The Healer',
            // Blooming Heart - Petals/Lotus SVG
            icon: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Center petal */}
                    <path d="M16 8C16 8 12 14 12 18C12 22 14 24 16 24C18 24 20 22 20 18C20 14 16 8 16 8Z" stroke="#D4AF37" strokeWidth="0.6" fill="rgba(212,175,55,0.1)"/>
                    {/* Left petal */}
                    <path d="M10 12C10 12 6 16 6 19C6 22 8 23 10 22C12 21 14 18 14 16C14 14 10 12 10 12Z" stroke="#D4AF37" strokeWidth="0.5" fill="rgba(212,175,55,0.05)" opacity="0.8"/>
                    {/* Right petal */}
                    <path d="M22 12C22 12 26 16 26 19C26 22 24 23 22 22C20 21 18 18 18 16C18 14 22 12 22 12Z" stroke="#D4AF37" strokeWidth="0.5" fill="rgba(212,175,55,0.05)" opacity="0.8"/>
                    {/* Outer glow petals */}
                    <path d="M8 16C6 14 4 16 5 19C6 21 8 20 8 16Z" stroke="#D4AF37" strokeWidth="0.3" fill="none" opacity="0.5"/>
                    <path d="M24 16C26 14 28 16 27 19C26 21 24 20 24 16Z" stroke="#D4AF37" strokeWidth="0.3" fill="none" opacity="0.5"/>
                    {/* Heart center glow */}
                    <circle cx="16" cy="18" r="2" fill="#D4AF37" opacity="0.6"/>
                </svg>
            ),
            gradient: 'radial-gradient(ellipse at center, #78350f 0%, #2a1b0b 45%, #000000 100%)',
            borderColor: 'rgba(212, 175, 55, 0.3)',
            glowColor: 'rgba(212, 175, 55, 0.2)',
            textColor: '#D4AF37',
            textShadow: '0 0 20px rgba(251, 191, 36, 0.4)'
        },
        {
            id: 'suenos',
            titleEs: 'SUEÑOS',
            titleEn: 'DREAMS',
            subtitleEs: 'El Místico',
            subtitleEn: 'The Mystic',
            // Crescent Eye - Moon cradling Eye SVG
            icon: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Crescent moon */}
                    <path d="M24 8C24 8 20 10 18 14C16 18 16 22 18 25C14 24 10 20 10 15C10 10 14 6 20 6C22 6 24 8 24 8Z" stroke="#9370DB" strokeWidth="0.6" fill="rgba(147,112,219,0.1)"/>
                    {/* Eye outer */}
                    <ellipse cx="18" cy="16" rx="5" ry="3" stroke="#9370DB" strokeWidth="0.5" fill="rgba(147,112,219,0.05)"/>
                    {/* Eye iris */}
                    <circle cx="18" cy="16" r="1.5" fill="#9370DB" opacity="0.7"/>
                    {/* Eye pupil */}
                    <circle cx="18" cy="16" r="0.6" fill="#E9D5FF"/>
                    {/* Sparkles */}
                    <circle cx="12" cy="10" r="0.4" fill="#9370DB" opacity="0.5"/>
                    <circle cx="24" cy="20" r="0.3" fill="#9370DB" opacity="0.4"/>
                    <circle cx="14" cy="24" r="0.35" fill="#9370DB" opacity="0.3"/>
                </svg>
            ),
            gradient: 'radial-gradient(ellipse at bottom, #312e81 0%, #0F0518 45%, #000000 100%)',
            borderColor: 'rgba(147, 112, 219, 0.3)',
            glowColor: 'rgba(147, 112, 219, 0.2)',
            textColor: '#9370DB',
            textShadow: 'none'
        }
    ];

    return (
        <>
            {/* 3-Pillar Entry Card */}
            <motion.div 
                className="w-full px-4 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
            >
                <h2 className="text-[10px] font-inter text-primary/50 uppercase tracking-[0.25em] mb-3">
                    {t('dashboard.dream_sanctuary_title')}
                </h2>
                
                {/* 3-Column Horizontal Row */}
                <div className="flex gap-2 w-full">
                    {pillars.map((pillar, index) => (
                        <motion.div
                            key={pillar.id}
                            className="flex-1 relative overflow-hidden rounded-xl cursor-pointer group"
                            style={{
                                background: pillar.gradient,
                                border: `1px solid ${pillar.borderColor}`,
                                boxShadow: `0 4px 20px rgba(0,0,0,0.3), inset 0 0 40px ${pillar.glowColor}`,
                                minHeight: '140px'
                            }}
                            onClick={() => setActiveSection(pillar.id)}
                            whileHover={{ 
                                scale: 1.02,
                                borderColor: pillar.textColor,
                                boxShadow: `0 8px 30px rgba(0,0,0,0.4), inset 0 0 60px ${pillar.glowColor}, 0 0 20px ${pillar.glowColor}`
                            }}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 * index }}
                            data-testid={`pillar-${pillar.id}`}
                        >
                            {/* Corner decorations */}
                            <div className="absolute top-2 left-2 w-4 h-4 border-t border-l opacity-40 group-hover:opacity-70 transition-opacity" style={{ borderColor: pillar.textColor }} />
                            <div className="absolute top-2 right-2 w-4 h-4 border-t border-r opacity-40 group-hover:opacity-70 transition-opacity" style={{ borderColor: pillar.textColor }} />
                            <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l opacity-40 group-hover:opacity-70 transition-opacity" style={{ borderColor: pillar.textColor }} />
                            <div className="absolute bottom-2 right-2 w-4 h-4 border-b border-r opacity-40 group-hover:opacity-70 transition-opacity" style={{ borderColor: pillar.textColor }} />
                            
                            {/* Content */}
                            <div className="relative z-10 h-full flex flex-col items-center justify-center p-4 text-center">
                                <motion.div 
                                    className="mb-3 opacity-80 group-hover:opacity-100 transition-opacity"
                                    animate={{ 
                                        filter: [`drop-shadow(0 0 8px ${pillar.glowColor})`, `drop-shadow(0 0 16px ${pillar.glowColor})`, `drop-shadow(0 0 8px ${pillar.glowColor})`]
                                    }}
                                    transition={{ duration: 3, repeat: Infinity }}
                                >
                                    {pillar.icon}
                                </motion.div>
                                
                                <h3 
                                    className="font-cinzel text-sm uppercase tracking-[0.3em] mb-1"
                                    style={{ 
                                        color: pillar.textColor,
                                        textShadow: pillar.textShadow
                                    }}
                                >
                                    {language === 'es' ? pillar.titleEs : pillar.titleEn}
                                </h3>
                                
                                <p 
                                    className="text-[9px] font-inter uppercase tracking-widest opacity-60"
                                    style={{ color: pillar.textColor }}
                                >
                                    {language === 'es' ? pillar.subtitleEs : pillar.subtitleEn}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Expanded Sanctuary Modal - Legacy */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div 
                        className="fixed inset-0 z-50 flex items-center justify-center p-4" 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }}
                    >
                        <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setIsExpanded(false)} />
                        
                        <motion.div 
                            className="relative w-full max-w-2xl h-[90vh] max-h-[800px] overflow-hidden rounded-2xl flex flex-col"
                            style={{ 
                                background: 'linear-gradient(180deg, rgba(20,18,30,0.98) 0%, rgba(12,10,20,0.99) 100%)', 
                                border: '1px solid rgba(212,175,55,0.25)'
                            }} 
                            initial={{ scale: 0.9, y: 50 }} 
                            animate={{ scale: 1, y: 0 }} 
                            exit={{ scale: 0.9, y: 50 }}
                        >
                            {/* Corner Decorations */}
                            <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-primary/30 rounded-tl-lg" />
                            <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-primary/30 rounded-tr-lg" />
                            <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-primary/30 rounded-bl-lg" />
                            <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-primary/30 rounded-br-lg" />
                            
                            {/* Header */}
                            <div className="relative z-10 p-6 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                                <div>
                                    <h3 className="font-cinzel text-2xl" style={{ background: 'linear-gradient(135deg, #F5D76E 0%, #D4AF37 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                        Dream Sanctuary
                                    </h3>
                                    <p className="text-xs font-inter text-white/40 uppercase tracking-[0.2em] mt-1">The Museum of Dreams</p>
                                </div>
                                <button 
                                    onClick={() => setIsExpanded(false)} 
                                    className="w-10 h-10 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            
                            {/* Tab Navigation - Removed Harmonic Chambers */}
                            <div className="relative z-10 flex border-b border-white/10 flex-shrink-0">
                                <button 
                                    onClick={() => setActiveTab('journal')} 
                                    className="flex-1 py-4 text-center relative transition-colors" 
                                    style={{ color: activeTab === 'journal' ? 'rgba(147,112,219,1)' : 'rgba(255,255,255,0.4)' }}
                                >
                                    <span className="text-base mr-2">📜</span>
                                    <span className="text-xs font-inter uppercase tracking-wider">The Astral Journal</span>
                                    {activeTab === 'journal' && <motion.div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(147,112,219,0.8), transparent)' }} layoutId="activeTab" />}
                                </button>
                                <button 
                                    onClick={() => setActiveTab('portal')} 
                                    className="flex-1 py-4 text-center relative transition-colors" 
                                    style={{ color: activeTab === 'portal' ? 'rgba(147,112,219,1)' : 'rgba(255,255,255,0.4)' }}
                                >
                                    <span className="text-base mr-2">👁</span>
                                    <span className="text-xs font-inter uppercase tracking-wider">Dream Portal</span>
                                    {activeTab === 'portal' && <motion.div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(147,112,219,0.8), transparent)' }} layoutId="activeTab" />}
                                </button>
                            </div>
                            
                            {/* Tab Content */}
                            <div className="relative z-10 flex-1 overflow-y-auto p-6">
                                <AnimatePresence mode="wait">
                                    {activeTab === 'journal' && (
                                        <motion.div key={`journal-${refreshKey}`} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}>
                                            <AstralJournal onOpenDream={() => setIsDreamOpen(true)} />
                                        </motion.div>
                                    )}
                                    {activeTab === 'portal' && (
                                        <motion.div key="portal" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}>
                                            <div className="py-6 space-y-6">
                                                <div className="text-center mb-8">
                                                    <span className="text-5xl mb-4 block">👁</span>
                                                    <h3 className="font-cinzel text-xl text-white/80 mb-2">Enter the Dream Portal</h3>
                                                    <p className="text-sm font-inter text-white/50 max-w-sm mx-auto">
                                                        Speak, write, or show your vision. The Oneiros will reveal the hidden messages within.
                                                    </p>
                                                </div>

                                                <motion.button
                                                    onClick={() => setIsDreamOpen(true)}
                                                    className="w-full py-5 rounded-xl font-inter text-sm uppercase tracking-wider"
                                                    style={{ background: 'linear-gradient(135deg, rgba(147,112,219,0.3) 0%, rgba(75,0,130,0.4) 100%)', border: '1px solid rgba(147,112,219,0.5)', color: 'white' }}
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <span className="mr-2">🌙</span> Interpret My Dream
                                                </motion.button>

                                                <div className="grid grid-cols-2 gap-4 pt-4">
                                                    <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(147,112,219,0.1)', border: '1px solid rgba(147,112,219,0.2)' }}>
                                                        <ImageIcon size={24} className="mx-auto mb-2 text-purple-400" />
                                                        <p className="text-xs text-white/60">Manifest Vision</p>
                                                        <p className="text-[10px] text-white/40 mt-1">Generate dream art</p>
                                                    </div>
                                                    <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)' }}>
                                                        <Eye size={24} className="mx-auto mb-2 text-primary" />
                                                        <p className="text-xs text-white/60">Unveil Hidden Truths</p>
                                                        <p className="text-[10px] text-white/40 mt-1">Deeper analysis</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Dream Interpreter Full Screen - Legacy (kept for backward compatibility) */}
            <AnimatePresence>
                {isDreamOpen && (
                    <DreamInterpreter 
                        onClose={() => setIsDreamOpen(false)} 
                        onDreamSaved={handleDreamSaved}
                    />
                )}
            </AnimatePresence>
            
            {/* Individual Section Modals - with prefilled query support */}
            <AnimatePresence>
                {activeSection === 'cosmos' && (
                    <CosmosChat onClose={handleCloseOracle} initialQuery={prefilledQuery} />
                )}
            </AnimatePresence>
            
            <AnimatePresence>
                {activeSection === 'vinculos' && (
                    <LoveChat onClose={handleCloseOracle} initialQuery={prefilledQuery} />
                )}
            </AnimatePresence>
            
            <AnimatePresence>
                {activeSection === 'suenos' && (
                    <DreamsChat onClose={handleCloseOracle} initialQuery={prefilledQuery} />
                )}
            </AnimatePresence>
        </>
    );
}

// Export DreamInterpreter as DreamSanctuaryChat for use in InsightsSection
export { DreamInterpreter as DreamSanctuaryChat };

export default DreamSanctuary;
